import { useEffect, useRef, useState } from "react"
import { Check, X } from "lucide-react"
import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"
import { useTranslation } from "react-i18next"

export type CoordinatePair = { lat: number; lon: number }

// Accepts "44.6039, 7.7645", "44.6039 7.7645" or "44,6039; 7,7645" as pasted
// from Google Maps, Marcos or a document. Both numbers must be decimal degrees
// in range; anything else (DMS, a single number, an address) is rejected.
export const parseCoordinatePair = (text: string): CoordinatePair | null => {
  const trimmed = text.trim()
  const match = trimmed.match(
    /^(-?\d{1,3}(?:[.,]\d+)?)\s*[,;\s]\s*(-?\d{1,3}(?:[.,]\d+)?)$/,
  )
  if (!match) return null
  const lat = Number(match[1].replace(",", "."))
  const lon = Number(match[2].replace(",", "."))
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null
  if (Math.abs(lat) > 90 || Math.abs(lon) > 180) return null
  return { lat, lon }
}

type StopCoordinatesEditorProps = {
  lat?: number
  lon?: number
  onSave: (pair: CoordinatePair) => void
  onCancel: () => void
}

// Inline "lat, lon" field shown in place of the coordinate preview. Enter or
// the check mark saves, Escape or the cross cancels. The stop card behind is
// clickable and draggable, so pointer events stay inside the editor.
export function StopCoordinatesEditor({
  lat,
  lon,
  onSave,
  onCancel,
}: StopCoordinatesEditorProps) {
  const { t } = useTranslation()
  const [text, setText] = useState(
    lat !== undefined && lon !== undefined ? `${lat}, ${lon}` : "",
  )
  const [invalid, setInvalid] = useState(false)
  const inputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    inputRef.current?.focus()
    inputRef.current?.select()
  }, [])

  const save = () => {
    const pair = parseCoordinatePair(text)
    if (!pair) {
      setInvalid(true)
      return
    }
    onSave(pair)
  }

  return (
    <div
      data-testid="stop-coordinates-editor"
      className="flex items-center gap-1"
      onClick={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.stopPropagation()}
    >
      <Input
        ref={inputRef}
        value={text}
        placeholder={t("maps.stopCoordinatesEditor.placeholder")}
        aria-label={t("maps.stopCoordinatesEditor.coordinates")}
        aria-invalid={invalid || undefined}
        className={cn(
          "h-6 w-44 px-1.5 text-xs",
          invalid && "border-red-500 focus-visible:ring-red-500",
        )}
        onChange={(event) => {
          setText(event.target.value)
          setInvalid(false)
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault()
            save()
          } else if (event.key === "Escape") {
            event.preventDefault()
            onCancel()
          }
        }}
      />
      <button
        type="button"
        aria-label={t("maps.stopCoordinatesEditor.saveCoordinates")}
        title={t("common.save")}
        onClick={save}
        className="inline-flex h-5 w-5 items-center justify-center rounded text-green-600 hover:bg-gray-100 dark:hover:bg-gray-800"
      >
        <Check className="h-3 w-3" />
      </button>
      <button
        type="button"
        aria-label={t("maps.stopCoordinatesEditor.cancelEditing")}
        title={t("common.cancel")}
        onClick={onCancel}
        className="inline-flex h-5 w-5 items-center justify-center rounded text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-800"
      >
        <X className="h-3 w-3" />
      </button>
      {invalid && (
        <span className="text-xs text-red-600">
          {t("maps.stopCoordinatesEditor.invalid")}
        </span>
      )}
    </div>
  )
}
